import {
  Banner,
  Box,
  Button,
  FlexBox,
  Page,
  Spinner,
} from '@dixa/lib-shared-habla';
import * as React from 'react';
import { connect } from 'react-redux';
import { Form, InjectedFormProps, reduxForm } from 'redux-form';

import {
  LandingCsatPreview
} from "components/settings/components/sections/satisfaction/components/common/LandingCsatPreview";

import { CustomizationPageSelectors } from '../../common/selectors';
import { EmailPanelForms } from '../EmailPanel/emailPanelForms';

import { EmailCustomizationFormView } from './components/EmailCustomizationForm';
import { EmailPreviewSection } from './components/EmailPreviewSection/EmailPreviewSection';
import { FeedbackSectionPreviewHeader } from './components/FeedbackSectionPreviewHeader/FeedbackSectionPreviewHeader';
import { FollowupCustomizationFormView } from './components/FollowupCustomizationForm';
import { EmptyGridChildPlaceholder } from './components/misc';
import style from './index.module.scss';
import { LanguageCustomizationForm } from './types';

export type OwnProps = {
  languageName?: string;
  settingsId?: string;
  mode?: 'create' | 'edit';
};

export type StateProps = {
  formValues: LanguageCustomizationForm.FormValues;
  initialValues: Partial<LanguageCustomizationForm.FormValues>;
  isLoading?: boolean;
};

export type DispatchProps = {
  onSubmit: (customization: LanguageCustomizationForm.FormValues) => void;
  loadEmailSettingsAllLevels: () => void;
};

type InjectedProps = InjectedFormProps<
  LanguageCustomizationForm.FormValues,
  CustomizationPageViewProps
>;

export type CustomizationPageViewProps = OwnProps & StateProps & DispatchProps;

type PropsT = CustomizationPageViewProps & InjectedProps;

const CustomizationPageView: React.FunctionComponent<
  PropsT & InjectedProps
> = ({
  formValues,
  languageName,
  handleSubmit,
  error: errorMessage,
  isLoading,
  pristine,
  mode,
}) => {
  if (isLoading) {
    return (
      <FlexBox height="100%" justifyContent="center" alignItems="center">
        <Spinner />
      </FlexBox>
    );
  }

  return (
    <>
      <Page.Section
        title={languageName}
        controls={
          <Button disabled={mode === 'edit' && pristine} onClick={handleSubmit}>
            Save customization
          </Button>
        }
      >
        {errorMessage && (
          <Box margin="0 0 400">
            <Banner
              variant="fail"
              icon={null}
              description={<>{errorMessage}</>}
            />
          </Box>
        )}

        <Form className={style.root} onSubmit={handleSubmit}>
          <EmailPreviewSection
            subjectText={formValues.subject}
            emailBodyText={formValues.body}
            ratingQuestion={formValues.ratingQuestion}
            ratingStyle={formValues.ratingStyle}
          />

          <EmailCustomizationFormView />
          <FeedbackSectionPreviewHeader className={style.withExtraGap} />
          <EmptyGridChildPlaceholder />

          <LandingCsatPreview
            thankYouText={formValues.headerText}
            questionText={formValues.subheaderText}
            placeholderText={formValues.placeholderText}
            submitButtonText={formValues.actionButtonText}
            ratingStyle={formValues.ratingStyle}
          />

          <FollowupCustomizationFormView />
        </Form>
      </Page.Section>
    </>
  );
};

export const CustomizationPageForm = reduxForm<
  LanguageCustomizationForm.FormValues,
  CustomizationPageViewProps
>({
  form: EmailPanelForms.LanguageCustomizationPageForm,
  enableReinitialize: true,
})(CustomizationPageView);

export const CustomizationPage = connect<
  StateProps,
  DispatchProps,
  OwnProps,
  CustomizationPageSelectors.State
>(
  (state, ownProps): StateProps => ({
    isLoading:
      state.settings.satisfaction.customization.email.languagesLevel
        .asyncStatus === 'loading',
    formValues:
      CustomizationPageSelectors.emailLanguageCustomizationFormValuesgetter(
        state,
      ),
    initialValues: CustomizationPageSelectors.initialEmailValuesSelector(
      state,
      ownProps,
    ),
  }),
)(CustomizationPageForm);
